import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button } from "../Button";
import { Typography } from "../Typography";
import { TextInput } from "../inputs/TextInput";
import { useFloating, autoUpdate, offset, shift, useDismiss, useRole, useClick, useInteractions, inline, arrow, } from "@floating-ui/react";
import { ThemeProviderContext } from "../ThemeProvider";
import { useCallback, useEffect, useLayoutEffect, useRef, useState, } from "react";
import { CustomPopover } from "./CustomPopover";
import { useLinkUrl } from "./useLinkUrl";
export const useAddLinkPopover = ({ editor, editorRef, }) => {
    const arrowRef = useRef(null);
    const [linkURL, setLinkURL] = useState("");
    const [showLinkPopover, setShowLinkPopover] = useState(false);
    const [selectedLinkText, setSelectedLinkText] = useState("");
    const [isLinkButtonDisabled, setIsLinkButtonDisabled] = useState(true);
    const [isEditingExistingLink, setIsEditingExistingLink] = useState(false);
    const { refs, context, strategy, x, y, middlewareData: { arrow: { x: arrowX, y: arrowY } = {} }, } = useFloating({
        strategy: "fixed",
        whileElementsMounted: autoUpdate,
        open: showLinkPopover,
        placement: "top",
        middleware: [offset(10), shift(), inline(), arrow({ element: arrowRef })],
    });
    const { getFloatingProps } = useInteractions([
        useClick(context, { enabled: true }),
        useRole(context),
        useDismiss(context, { enabled: true }),
    ]);
    const { httpsUrl } = useLinkUrl(linkURL);
    /* HIGHLIGHTS START: Highlight the selected text while popover is open */
    useLayoutEffect(() => {
        const onAddLinkPopoverOpened = () => {
            var _a;
            if (!editor)
                return;
            if (editor.isActive("link")) {
                editor
                    .chain()
                    .extendMarkRange("link")
                    .setHighlight()
                    .setMeta("addToHistory", false)
                    .run();
            }
            else {
                editor.chain().setHighlight().setMeta("addToHistory", false).run();
            }
            const textElement = editor.view.dom.querySelector(".highlighted");
            if (textElement) {
                refs.setReference({
                    getBoundingClientRect: () => textElement.getBoundingClientRect(),
                    getClientRects: () => textElement.getClientRects(),
                });
            }
            (_a = window.getSelection()) === null || _a === void 0 ? void 0 : _a.empty();
        };
        const onAddLinkPopoverClosed = () => {
            if (!editor)
                return;
            editor
                .chain()
                .focus()
                .unsetAllHighlights()
                .setMeta("addToHistory", false)
                .run();
        };
        if (showLinkPopover) {
            onAddLinkPopoverOpened();
        }
        else {
            onAddLinkPopoverClosed();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showLinkPopover]);
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (!editor)
                return;
            // Hitting "undo" will reapply the highlight used for hyperlinks
            // So we clear them just in case
            if ((e.ctrlKey || e.metaKey) && e.key === "z") {
                editor.commands.unsetAllHighlights();
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [editor]);
    /* HIGHLIGHTS END */
    /* TEXT TRACKING START: Keep track of the selected text and link in case they open the popover */
    useEffect(() => {
        const handleSelectionUpdate = () => {
            if (!editor)
                return;
            const existingLink = editor.isActive("link")
                ? editor.getAttributes("link")
                : null;
            let textSelection = "";
            let linkUrl = "";
            let shouldDisableToolbarLinkButton = false;
            if (existingLink) {
                const linkHasBeenSelectedBySingleClick = editor.view.state.selection.$from.pos ===
                    editor.view.state.selection.$to.pos;
                // how we get the existing link text depends on if the user has clicked on the linked text
                // or if they are dragging their mouse to select text
                const position = linkHasBeenSelectedBySingleClick
                    ? editor.view.state.selection.$anchor.pos
                    : editor.view.state.selection.$from.pos + 1;
                const linkNode = editor.view.state.doc.nodeAt(position);
                textSelection = (linkNode === null || linkNode === void 0 ? void 0 : linkNode.text) || "";
                linkUrl = existingLink.href;
                // we want to disable the link button in the editor toolbar if the user has clicked
                // on the linked text because the EditLinkPopover will open instead
                shouldDisableToolbarLinkButton = linkHasBeenSelectedBySingleClick;
            }
            else {
                textSelection = editor.state.doc.textBetween(editor.state.selection.from, editor.state.selection.to, " ");
            }
            setIsEditingExistingLink(!!existingLink);
            setSelectedLinkText(textSelection);
            setLinkURL(linkUrl);
            setIsLinkButtonDisabled(shouldDisableToolbarLinkButton || textSelection === "");
        };
        const handleBlur = () => {
            setIsLinkButtonDisabled(!showLinkPopover);
        };
        if (!showLinkPopover) {
            editor === null || editor === void 0 ? void 0 : editor.on("selectionUpdate", handleSelectionUpdate);
            editor === null || editor === void 0 ? void 0 : editor.on("blur", handleBlur);
        }
        return () => {
            editor === null || editor === void 0 ? void 0 : editor.off("selectionUpdate", handleSelectionUpdate);
            editor === null || editor === void 0 ? void 0 : editor.off("blur", handleBlur);
        };
    }, [editor, showLinkPopover]);
    /* TEXT TRACKING END */
    const handleCloseLinkPopover = () => {
        setShowLinkPopover(false);
    };
    const handleApplyLink = useCallback(() => {
        if (!editor)
            return;
        if (linkURL === "") {
            editor.chain().focus().extendMarkRange("link").unsetLink().run();
            handleCloseLinkPopover();
            return;
        }
        editor
            .chain()
            .focus()
            .extendMarkRange("link")
            .setLink({
            href: httpsUrl,
        })
            .command(({ tr }) => {
            const pos = editor.view.state.selection.$anchor.pos;
            const linkNode = editor.view.state.doc.nodeAt(pos);
            if (linkNode) {
                const textNode = editor.state.schema.text((selectedLinkText || linkURL), linkNode.marks);
                tr.replaceSelectionWith(textNode);
            }
            else {
                tr.insertText(selectedLinkText, editor.state.selection.from, editor.state.selection.to);
            }
            return true;
        })
            .run();
        setShowLinkPopover(false);
        setSelectedLinkText("");
        setLinkURL("");
    }, [editor, httpsUrl, linkURL, selectedLinkText]);
    const onClickEditLink = (values) => {
        setShowLinkPopover(true);
        setSelectedLinkText(values.text);
        setLinkURL(values.href);
        setIsEditingExistingLink(true);
    };
    const handleClickLinkButton = () => {
        setShowLinkPopover(!showLinkPopover);
    };
    return {
        AddLinkPopover,
        addLinkPopoverProps: {
            context,
            getFloatingProps,
            refs,
            strategy,
            y,
            x,
            arrowRef,
            handleCloseLinkPopover,
            selectedLinkText,
            setSelectedLinkText,
            linkURL,
            setLinkURL,
            handleApplyLink,
            arrowX,
            arrowY,
            isEditingExistingLink,
        },
        editorToolbarProps: {
            isLinkButtonDisabled,
            handleClickLinkButton,
        },
        resetAddLinkPopover: handleCloseLinkPopover,
        onClickEditLink,
        showLinkPopover,
    };
};
const AddLinkPopover = ({ context, getFloatingProps, refs, strategy, y, x, arrowRef, handleCloseLinkPopover, selectedLinkText, setSelectedLinkText, linkURL, setLinkURL, handleApplyLink, arrowX, arrowY, isEditingExistingLink, }) => {
    return (_jsx(ThemeProviderContext.Consumer, { children: ({ menuZIndex, translate }) => (_jsxs(CustomPopover, { menuZIndex: menuZIndex, context: context, refs: refs, getFloatingProps: getFloatingProps, strategy: strategy, x: x, y: y, arrowRef: arrowRef, arrowX: arrowX, arrowY: arrowY, id: "add-link-tooltip", popoverClassName: "!p-2", children: [_jsxs("div", { className: "flex items-center justify-between w-[316px]", children: [_jsx(Typography, { variant: "body-large-emphasis", children: isEditingExistingLink
                                ? translate("components.rich_text_editor.add_link_popover.edit_link")
                                : translate("components.rich_text_editor.add_link_popover.add_link") }), _jsx(Button, { text: "", variant: "tertiary-neutral", iconName: "Close", size: "medium", onClick: handleCloseLinkPopover })] }), _jsx(TextInput, { label: translate("components.rich_text_editor.add_link_popover.text"), value: selectedLinkText, onChange: (event) => setSelectedLinkText(event.value) }), _jsx(TextInput, { label: translate("components.rich_text_editor.add_link_popover.link"), value: linkURL, onChange: (event) => setLinkURL(event.value) }), _jsxs("div", { className: "pt-2 flex justify-end gap-1", children: [_jsx(Button, { variant: "secondary", shape: "pill", text: translate("components.rich_text_editor.add_link_popover.cancel"), size: "small", onClick: handleCloseLinkPopover }), _jsx(Button, { variant: "primary", shape: "pill", text: translate("components.rich_text_editor.add_link_popover.apply"), size: "small", onClick: handleApplyLink })] })] })) }));
};
//# sourceMappingURL=useAddLinkPopover.js.map